import { IRefIds } from 'constants/refs';
import { del, get, post, put } from 'services/request';
import { BaseModel, IModelCourseOptions } from './BaseModel';

export type IConjugationRootChangeId = { id: number };

export type IConjugationRootChange = IConjugationRootChangeId & {
  fromText: string;
  toText: string;
  contextNextEndingStartsWith: string;
  priority: number;
  wordClassId: number | null;
  wordClassName?: string | null;
};

export type IConjugationRootChangeEdit = IConjugationRootChangeId & {
  fromText: string;
  toText: string;
  contextNextEndingStartsWith: string;
  priority: number;
  deletable: boolean;
  wordClass: number | null;
  wordClassName?: string | null;
};

type IModelCourseLanguageOptions = IModelCourseOptions & {
  languageId: number;
};

export default class ConjugationRootChangesModel extends BaseModel<IConjugationRootChange, IConjugationRootChangeEdit> {
  languageId: number;

  constructor(options: IModelCourseLanguageOptions) {
    super(options);
    this.ref = IRefIds.conjugationRootChanges;
    this.gridName = 'ConjugationRootChanges';
    this.languageId = options.languageId;
    this.getGrid();
    this.getGridColumns();
  }

  async getGrid() {
    await super.getGrid();
    const items = await get<IConjugationRootChange[]>(`languages/${this.languageId}/conjugation-root-changes`);
    this.gridData = items.sort((a, b) => {
      const wordClassA = a.wordClassName ?? '';
      const wordClassB = b.wordClassName ?? '';
      if (wordClassA === wordClassB) {
        return a.priority - b.priority;
      }
      return wordClassA.localeCompare(wordClassB);
    });
    this.loadingGrid = false;
    this.render();
  }

  async getGridRow(itemId: number) {
    return await get<IConjugationRootChange>(`languages/${this.languageId}/conjugation-root-changes/${itemId}`);
  }

  async reload() {
    await super.reload();
    await this.getGrid();
  }

  async new() {
    await super.new();
    const item = await get<IConjugationRootChangeEdit>(`conjugation-root-changes/new`);
    await this.setItem(item);
    this.render();
  }

  async save() {
    await super.save();
    const { id } = this.itemUpdated;
    if (id === this.NEW_CREATE_ID) {
      await this.createItem();
    } else {
      await this.updateItem();
    }
  }

  private async updateItem() {
    const { id } = this.itemUpdated;
    await put(`conjugation-root-changes/${id}`, this.itemUpdated);
    await Promise.all([this.updateGridRow(id, 'update'), this.loadItemEdit(id)]);
  }

  private async createItem() {
    const result = await post<IConjugationRootChangeId>(
      `languages/${this.languageId}/conjugation-root-changes`,
      this.itemUpdated,
    );

    if (result.ok && result.data) {
      const newItemId = result.data.id;
      await Promise.all([this.updateGridRow(newItemId, 'add'), this.loadItemEdit(newItemId)]);
    }
  }

  async delete() {
    const { id } = this.itemUpdated;
    const result = await del(`conjugation-root-changes/${id}`);
    if (result.ok) {
      await Promise.all([this.updateGridRow(id, 'remove'), this.new()]);
    } else {
      console.error(JSON.stringify(result.data));
    }
  }

  async loadItemData(itemId: number) {
    return await get<IConjugationRootChangeEdit>(`conjugation-root-changes/${itemId}`);
  }
}
